import { ArrowBack } from '@mui/icons-material'
import {
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Switch,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

import DviPage from '../../components/DviPage/DviPage'
import DviVehicleUpdateButton from '../../components/Inspections/DviVehicleUpdateButton'
import { InspectionStatusSelect } from '../../components/Inspections/InspectionStatusSelect'
import { InspectionSummaryBar } from '../../components/Inspections/InspectionSummaryBar'
import { ClockInForRepairOrderDialog } from '../../components/TimeClockCard/ClockInForRepairOrderDialog'
import { AppRoutes } from '../../models'
import {
  InspectionStatusWithEntries,
  InspectionWithRelated
} from '../../models/inspections'
import { CustomerEntry, RepairOrderExpanded, VehicleEntry } from '../../sdk'
import { describeVehicle } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import CompleteInspectionButton from './components/CompleteInspectionButton'
import SingleSection from './components/SingleSection'

export const DviSingleInspectionContent = ({
  inspection,
  repairOrder,
  vehicle,
  isLoading,
  customer,
  entriesByStatus,
  isCached
}: {
  inspection: InspectionWithRelated | undefined
  repairOrder: RepairOrderExpanded | undefined
  vehicle: VehicleEntry | undefined
  isLoading: boolean
  customer: CustomerEntry | undefined
  entriesByStatus: InspectionStatusWithEntries[]
  isCached?: boolean
}) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [isTimeClockChecked, setIsTimeClockChecked] = useState(false)
  const [showIncomplete, setShowIncomplete] = useState(false)
  return (
    <DviPage
      isLoading={isLoading}
      title="Inspection Checklist"
      actionsRight={
        <IconButton
          onClick={() =>
            navigate(
              generatePath(AppRoutes.DVI_SINGLE_INSPECTION_SUMMARY, {
                id: id!
              })
            )
          }
          sx={{ color: 'primary.light' }}>
          <ArrowBack />
        </IconButton>
      }>
      {!isTimeClockChecked && inspection?.info!.repairOrder && (
        <ClockInForRepairOrderDialog
          setIsTimeClockChecked={setIsTimeClockChecked}
          repairOrder={inspection.info!.repairOrder}
        />
      )}
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography component="h2" variant="h2">
            {inspection?.info!.title}
          </Typography>
          {inspection && (
            <InspectionStatusSelect inspection={inspection} isChip readOnly />
          )}
        </Stack>
        <Typography>{inspection?.info!.description}</Typography>
        <Stack direction="row" justifyContent={'space-between'}>
          {repairOrder && (
            <Typography variant="h6">{`RO ID: #${repairOrder.entry!
              .localId!}`}</Typography>
          )}
          {vehicle && (
            <Typography variant="h6">
              {describeVehicle(vehicle.info)}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          justifyContent={'space-between'}
          alignItems={'center'}>
          <Stack>
            <Typography variant="subtitle1">Customer:</Typography>
            <Typography>{formatName(customer?.information)}</Typography>
          </Stack>
          <DviVehicleUpdateButton
            vehicleId={vehicle?.id!}
            mergeCallback={() => window.location.reload()}
            variant="outlined">
            Edit Vehicle Info
          </DviVehicleUpdateButton>
        </Stack>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={showIncomplete}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setShowIncomplete(event.target.checked)
                }}
              />
            }
            label="Show only incomplete required entries"
          />
        </FormGroup>
        {inspection && (
          <Box>
            {inspection.sections.map((section) => (
              <SingleSection
                key={section.id!}
                section={section}
                showIncomplete={showIncomplete}
                isCached={isCached}
              />
            ))}
          </Box>
        )}
        {entriesByStatus && (
          <InspectionSummaryBar entriesByStatus={entriesByStatus} />
        )}
        <CompleteInspectionButton
          {...{ showIncomplete, setShowIncomplete, inspection, isCached }}
        />
      </Stack>
    </DviPage>
  )
}
